import { Injectable } from '@angular/core';
import { Observable, Subject, startWith, switchMap } from 'rxjs';
import { HttpEvent } from '@angular/common/http';
import { CompanyCropSelectionsResponse, JobEnhancementOptionsResponse, JobListResponse, RequeueFailedStageResponse, SubJobItem } from '../models/job.models';
import { CheckoutSessionResponse, DownloadCheckoutSessionResponse, JobFormResponse } from '../models/job-form.models';
import { JobPhotosResponse } from '../models/job-photo.models';
import { JobErrorLogsResponse } from '../models/job-error-log.models';
import {
  UploadJobImagesResponse,
  JobUploadSessionResponse,
  CompleteJobUploadSessionResponse,
  StartOrResumeUploadSessionRequest,
  PrepareFilesRequest,
  PrepareFilesResponse,
  ConfirmFileResponse,
  PendingFilesResponse,
  LogUploadErrorRequest
} from '../models/job-image-upload.models';
import { JobsApiService } from './jobs-api.service';
import { ApiResponse } from '../../../companies/data/models/company.models';
import { PresetCategoriesListResponse, PresetsListResponse, CompanyEnhancementSettings } from '../models/preset.models';

@Injectable({
  providedIn: 'root'
})
export class JobsService {
  private readonly jobsRefreshSubject = new Subject<void>();

  constructor(private jobsApiService: JobsApiService) { }

  getJobs(companyId: number, pageNumber: number = 1, pageSize: number = 50): Observable<JobListResponse> {
    return this.jobsApiService.getJobs(companyId, pageNumber, pageSize);
  }

  getSubJobs(jobId: number): Observable<ApiResponse<SubJobItem[]>> {
    return this.jobsApiService.getSubJobs(jobId);
  }

  getJobsWithRefresh(companyId: number, pageNumber: number = 1, pageSize: number = 50): Observable<JobListResponse> {
    return this.jobsRefreshSubject.pipe(
      startWith(void 0),
      switchMap(() => this.jobsApiService.getJobs(companyId, pageNumber, pageSize))
    );
  }

  getEnhancementOptions(companyId: number): Observable<JobEnhancementOptionsResponse> {
    return this.jobsApiService.getEnhancementOptions(companyId);
  }

  getPresetsByCategoryId(presetCategoryId: number): Observable<ApiResponse<PresetsListResponse>> {
    return this.jobsApiService.getPresetsByCategoryId(presetCategoryId);
  }

  getCropSelections(jobId: number, companyId: number): Observable<CompanyCropSelectionsResponse> {
    return this.jobsApiService.getCropSelections(jobId, companyId);
  }

  getCompanyEnhancementSettings(companyId: number): Observable<ApiResponse<CompanyEnhancementSettings>> {
    return this.jobsApiService.getCompanyEnhancementSettings(companyId);
  }

  runEnhancementTest(companyId: number, presetName: string, file: File): Observable<ApiResponse<CompanyEnhancementSettings>> {
    return this.jobsApiService.runEnhancementTest(companyId, presetName, file);
  }

  saveEnhancementPreset(jobId: number, presetName: string, companyId: number): Observable<{ success: boolean; message: string }> {
    return this.jobsApiService.saveEnhancementPreset(jobId, presetName, companyId);
  }

  saveCropSelections(jobId: number, selections: any[], companyId: number): Observable<{ success: boolean; message: string; data: any }> {
    return this.jobsApiService.saveCropSelections(jobId, selections, companyId);
  }

  refreshJobs(): void {
    this.jobsRefreshSubject.next();
  }

  getJobById(jobId: number): Observable<JobFormResponse> {
    return this.jobsApiService.getJobById(jobId);
  }

  createJob(payload: JobFormResponse): Observable<{ success: boolean; id: number }> {
    return this.jobsApiService.createJob(payload);
  }

  updateJob(jobId: number, payload: JobFormResponse): Observable<{ success: boolean }> {
    return this.jobsApiService.updateJob(jobId, payload);
  }

  deleteJob(jobId: number): Observable<{ success: boolean }> {
    return this.jobsApiService.deleteJob(jobId);
  }

  getJobPhotos(jobId: number): Observable<JobPhotosResponse> {
    return this.jobsApiService.getJobPhotos(jobId);
  }

  getActiveJobUploadSession(jobId: number, companyId: number): Observable<ApiResponse<JobUploadSessionResponse>> {
    return this.jobsApiService.getActiveJobUploadSession(jobId, companyId);
  }

  createJobUploadSession(jobId: number, companyId: number, expectedFileCount: number): Observable<ApiResponse<JobUploadSessionResponse>> {
    return this.jobsApiService.createJobUploadSession(jobId, companyId, expectedFileCount);
  }

  getJobUploadSession(jobId: number, sessionId: number, companyId: number): Observable<ApiResponse<JobUploadSessionResponse>> {
    return this.jobsApiService.getJobUploadSession(jobId, sessionId, companyId);
  }

  getJobUploadSessionItems(jobId: number, sessionId: number, companyId: number): Observable<ApiResponse<any>> {
    return this.jobsApiService.getJobUploadSessionItems(jobId, sessionId, companyId);
  }

  completeJobUploadSession(jobId: number, sessionId: number, companyId: number): Observable<ApiResponse<CompleteJobUploadSessionResponse>> {
    return this.jobsApiService.completeJobUploadSession(jobId, sessionId, companyId);
  }

  startOrResumeUploadSession(
    jobId: number,
    body: StartOrResumeUploadSessionRequest
  ): Observable<ApiResponse<JobUploadSessionResponse>> {
    return this.jobsApiService.startOrResumeUploadSession(jobId, body);
  }

  prepareFiles(
    jobId: number,
    sessionId: number,
    body: PrepareFilesRequest
  ): Observable<ApiResponse<PrepareFilesResponse>> {
    return this.jobsApiService.prepareFiles(jobId, sessionId, body);
  }

  confirmFile(
    jobId: number,
    sessionId: number,
    companyId: number,
    jobUploadSessionItemId: number
  ): Observable<ApiResponse<ConfirmFileResponse>> {
    return this.jobsApiService.confirmFile(jobId, sessionId, { companyId, jobUploadSessionItemId });
  }

  getPendingFiles(
    jobId: number,
    sessionId: number
  ): Observable<ApiResponse<PendingFilesResponse>> {
    return this.jobsApiService.getPendingFiles(jobId, sessionId);
  }

  refreshUploadUrls(
    jobId: number,
    sessionId: number
  ): Observable<ApiResponse<PendingFilesResponse>> {
    return this.jobsApiService.refreshUploadUrls(jobId, sessionId);
  }

  logUploadError(
    jobId: number,
    sessionId: number,
    body: LogUploadErrorRequest
  ): Observable<ApiResponse<any>> {
    return this.jobsApiService.logUploadError(jobId, sessionId, body);
  }

  getErrorLogs(params: {
    jobId?: number | null;
    jobImageId?: number | null;
    processType?: string | null;
    stage?: string | null;
    status?: string | null;
    errorCode?: string | null;
    sourceWorker?: string | null;
    isResolved?: boolean | null;
    pageNumber: number;
    pageSize: number;
  }): Observable<ApiResponse<JobErrorLogsResponse>> {
    return this.jobsApiService.getErrorLogs(params);
  }

  uploadJobImages(jobId: number, companyId: number, jobUploadSessionId: number, files: File[]): Observable<HttpEvent<ApiResponse<UploadJobImagesResponse>>> {
    return this.jobsApiService.uploadJobImages(jobId, companyId, jobUploadSessionId, files);
  }


  submitJobProcessing(jobId: number): Observable<ApiResponse<any>> {
    return this.jobsApiService.submitJobProcessing(jobId);
  }

  requeueFailedStage(jobId: number, stage: string): Observable<ApiResponse<RequeueFailedStageResponse>> {
    return this.jobsApiService.requeueFailedStage(jobId, stage);
  }

  private attachedFiles: File[] = [];

  setAttachedFiles(files: File[]): void {
    this.attachedFiles = files;
  }

  getAttachedFiles(): File[] {
    return this.attachedFiles;
  }

  clearAttachedFiles(): void {
    this.attachedFiles = [];
  }

  createCheckoutSession(jobId: number, amount: number, successUrl: string, cancelUrl: string): Observable<CheckoutSessionResponse> {
    return this.jobsApiService.createCheckoutSession(jobId, amount, successUrl, cancelUrl);
  }

  verifyPaymentStatus(jobId: number, sessionId: string): Observable<{ success: boolean; isPaid: boolean; transactionId?: string; downloadUrl?: string }> {
    return this.jobsApiService.verifyPaymentStatus(jobId, sessionId);
  }

  getDownloadCheckoutSession(jobId: number, companyId: number, successUrl?: string, cancelUrl?: string): Observable<DownloadCheckoutSessionResponse> {
    return this.jobsApiService.getDownloadCheckoutSession(jobId, companyId, successUrl, cancelUrl);
  }
} 